import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components/macro';
import { useHistory, useParams } from 'react-router-dom';
import partial from 'lodash-es/partial';
import ChannelView from 'pages/Conversations/components/ChannelView';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import {
  ChannelType,
  ChannelVisibility,
  MutationType,
  useGetTeamChannelsQuery,
  useSubscribeToTeamChannelsSubscription,
} from 'shared/types/graphql';
import useClinicUser from 'shared/hooks/useClinicUser';
import { RouteBasePaths } from 'routes';
import { PersistedStateKeys } from 'shared/enums/PersistedStateKeys';
import { DeviceSizes } from 'shared/enums/DeviceSizes';
import usePersistedState from 'shared/hooks/usePersistedState';
import AddTeamChannelModal from './AddTeamChannelModal';
import EditTeamChannelModal from './EditTeamChannelModal';
import useUnreadMessageCount from 'shared/hooks/useUnreadMessageCount';
import { useResolutionProvider } from 'shared/providers/ResolutionProvider';
import CreateDirectMessageModal from './CreateDirectMessageModal';
import { buildDirectMessageName } from './utils/directMessages';
import { useInAppNotificationsProvider } from 'shared/providers/InAppNotificationsProvider';
import { NotificationCounter } from '@televet/kibble-ui/build/components/NotificationCounter';
import { Box, HStack, Flex, useMediaQuery, useDisclosure } from '@televet/kibble-ui/build/chakra';
import { Text } from '@televet/kibble-ui/build/components/Text';
import { Icon } from '@televet/kibble-ui/build/components/Icon';
import { Tooltip } from '@televet/kibble-ui/build/components/Tooltip';
import { AvatarGroup } from '@televet/kibble-ui/build/components/AvatarGroup';
import { Heading } from '@televet/kibble-ui/build/components/Heading';
import { Button } from '@televet/kibble-ui/build/components/Button';

const UNNAMED_CHANNEL_LABEL = 'Unnamed Channel';

type TeamModalNames = 'AddTeamChannel' | 'EditTeamChannel' | 'CreateDirectMessage';

const TeamPage = (): JSX.Element | null => {
  const { currentClinicId, clinicUser } = useClinicUser();
  const { channelId } = useParams<{ channelId?: string }>();
  const { isMobile } = useResolutionProvider();
  const { setExcludedChannelIds } = useInAppNotificationsProvider();
  const [lastSelectedTeamChannelIds, setLastSelectedTeamChannelIds] = usePersistedState<Record<string, string>>(
    PersistedStateKeys.LastSelectedTeamChannelIds,
    {},
  );
  const history = useHistory();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [modalName, setModalName] = useState<TeamModalNames | undefined>();
  const {
    data: { unreadChannelArray: unreadChannelIds },
  } = useUnreadMessageCount();

  const [isMobileWidth] = useMediaQuery('(' + DeviceSizes.MobileMaxWidth + ')');

  const goToChannel = useCallback(
    (channelId: string): void => {
      history.push(`${RouteBasePaths.Team}/${channelId}`);
      if (currentClinicId && lastSelectedTeamChannelIds[currentClinicId] !== channelId) {
        setLastSelectedTeamChannelIds({ ...lastSelectedTeamChannelIds, [currentClinicId]: channelId });
      }
    },
    [history, lastSelectedTeamChannelIds, currentClinicId, setLastSelectedTeamChannelIds],
  );

  // Ensure user doesn't get notifications for the current channel
  useEffect(() => {
    if (channelId) setExcludedChannelIds([channelId]);
    return (): void => setExcludedChannelIds([]);
  }, [channelId, setExcludedChannelIds]);

  // Redirect to the user's last selected channel when navigating to /team
  useEffect(() => {
    if (
      !channelId &&
      lastSelectedTeamChannelIds &&
      currentClinicId &&
      lastSelectedTeamChannelIds[currentClinicId] &&
      !isMobile
    ) {
      goToChannel(lastSelectedTeamChannelIds[currentClinicId]);
    }
  }, [channelId, lastSelectedTeamChannelIds, currentClinicId, isMobile, goToChannel]);

  const commonQueryVariables = {
    clinicId: {
      equals: currentClinicId,
    },
    channelType: {
      equals: ChannelType.Team,
    },
    isActive: {
      equals: true,
    },
  };

  const { data, refetch: refetchTeamChannels } = useGetTeamChannelsQuery({
    fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
    skip: !currentClinicId || !clinicUser?.id,
    variables: {
      wherePublic: {
        ...commonQueryVariables,
        channelVisibility: {
          equals: ChannelVisibility.Public,
        },
      },
      wherePrivate: {
        ...commonQueryVariables,
        channelVisibility: {
          equals: ChannelVisibility.Private,
        },
        assignees: {
          some: {
            id: {
              equals: clinicUser?.id,
            },
          },
        },
      },
      whereDirectMessages: {
        ...commonQueryVariables,
        channelVisibility: {
          equals: ChannelVisibility.DirectMessage,
        },
        assignees: {
          some: {
            id: {
              equals: clinicUser?.id,
            },
          },
        },
      },
    },
    onCompleted: (data) => {
      const channels =
        data.publicChannels && data.privateChannels && data.directMessages
          ? [...data.publicChannels, ...data.privateChannels, ...data.directMessages]
          : [];

      if (channels.length) {
        const [firstChannel] = channels;
        const isChannelIdInCurrentChannels = channelId && channels.some((channel) => channel.id === channelId);
        if (((!channelId && firstChannel) || !isChannelIdInCurrentChannels) && !isMobile) {
          goToChannel(firstChannel.id);
        }
      }
    },
  });

  const sortedChannels = useMemo(() => {
    const channels =
      data?.publicChannels && data?.privateChannels ? [...data.publicChannels, ...data.privateChannels] : [];
    return (channels.slice() || []).sort(({ friendlyName: fnA }, { friendlyName: fnB }) => {
      return (fnA || UNNAMED_CHANNEL_LABEL).localeCompare(fnB || UNNAMED_CHANNEL_LABEL, undefined, { numeric: true });
    });
  }, [data]);

  const sortedDirectMessages = useMemo(() => {
    const directMessages = data?.directMessages ? data.directMessages : [];
    return (directMessages.slice() || []).sort(({ friendlyName: fnA }, { friendlyName: fnB }) => {
      return (fnA || UNNAMED_CHANNEL_LABEL).localeCompare(fnB || UNNAMED_CHANNEL_LABEL, undefined, { numeric: true });
    });
  }, [data]);

  useSubscribeToTeamChannelsSubscription({
    variables: {
      where: {
        node: {
          clinicId: { equals: currentClinicId },
          channelType: { equals: ChannelType.Team },
        },
      },
    },
    skip: !currentClinicId,
    onData: ({ data }) => {
      if (
        data.data?.channelChanged?.mutation === MutationType.Create ||
        data.data?.channelChanged?.mutation === MutationType.Update
      ) {
        refetchTeamChannels();
      }
    },
  });

  const handleAddChannelClick = async (): Promise<void> => {
    setModalName('AddTeamChannel');
    onOpen();
  };

  const handleCreateDirectMessageClick = async (): Promise<void> => {
    setModalName('CreateDirectMessage');
    onOpen();
  };

  const handleEditTeamChannel = (): void => {
    setModalName('EditTeamChannel');
    onOpen();
  };

  const handleModalClose = (): void => {
    setModalName(undefined);
    onClose();
  };

  const handleAddTeamChannelSuccess = (newChannelId: string): void => {
    refetchTeamChannels();
    goToChannel(newChannelId);
  };

  const showChannelList = !isMobile || !channelId;
  const showChannelView = !isMobile || channelId;

  return (
    <TeamPageContainer>
      {showChannelList && (
        <Flex
          flex="0 0 auto"
          flexDir="column"
          width={isMobileWidth ? '100%' : '250px'}
          borderRight="1px"
          borderColor="border.default"
          overflow="auto"
        >
          <Flex flexDir="column" pb={5} data-testid="channels-section">
            <Flex align="center" justify="space-between" mr={5} mb={2} ml={7} pt={5}>
              <Flex>
                <Heading textTransform="uppercase" size="xs" fontWeight="bold">
                  Channels
                </Heading>
              </Flex>
              <Tooltip label="Add team channel">
                <Button
                  variant="ghostNeutral"
                  iconName="plus"
                  onClick={handleAddChannelClick}
                  data-testid="add-channel-button"
                  size={isMobile ? 'sm' : 'md'}
                >
                  {isMobile && 'Add Channel'}
                </Button>
              </Tooltip>
            </Flex>
            <Flex flexDir="column" mb={5} data-testid="channels-list">
              {sortedChannels.map(({ id, friendlyName, channelVisibility }) => {
                const hasUnreadMessages = unreadChannelIds ? unreadChannelIds.includes(id) : false;
                const isActiveChannel = id === channelId;

                return (
                  <HStack
                    align="center"
                    justify="space-between"
                    cursor="pointer"
                    py={2}
                    px={6}
                    key={id}
                    data-testid="team-channel-list-item"
                    className="Team__TeamChannelList-Item"
                    bg={isActiveChannel ? 'background.primaryAlpha.pressed' : 'transparent'}
                    _hover={{
                      bg: 'background.primaryAlpha.hover',
                    }}
                    onClick={partial(goToChannel, id)}
                  >
                    <HStack align="center">
                      <Tooltip label={`${channelVisibility} Channel`}>
                        <Icon
                          size="sm"
                          name={channelVisibility === ChannelVisibility.Private ? 'lock' : 'hashtag'}
                          color={isActiveChannel ? 'text.brand' : 'text.default'}
                          style={{ position: 'relative' }}
                        />
                      </Tooltip>
                      <Text color={isActiveChannel ? 'text.brand' : 'text.default'} isTruncated>
                        {friendlyName || UNNAMED_CHANNEL_LABEL}
                      </Text>
                    </HStack>
                    <Box>
                      {hasUnreadMessages && (
                        <Tooltip label="This conversation has unread messages">
                          <span>
                            <NotificationCounter size="sm" />
                          </span>
                        </Tooltip>
                      )}
                    </Box>
                  </HStack>
                );
              })}
            </Flex>
          </Flex>
          <Flex flexDir="column" pb={5} data-testid="direct-message-section">
            <Flex
              align="center"
              justify="space-between"
              mr={5}
              mb={2}
              ml={7}
              pt={5}
              borderTop="1px"
              borderColor="border.default"
            >
              <Heading textTransform="uppercase" size="xs" fontWeight="bold">
                Direct Messages
              </Heading>
              <Tooltip label="Create direct message">
                <Button
                  variant="ghostNeutral"
                  iconName="plus"
                  onClick={handleCreateDirectMessageClick}
                  data-testid="add-channel-button"
                  size={isMobile ? 'sm' : 'md'}
                >
                  {isMobile && 'Create direct message'}
                </Button>
              </Tooltip>
            </Flex>

            <Flex flexDir="column" mb={6}>
              {sortedDirectMessages.map(({ id, assignees }) => {
                const hasUnreadMessages = unreadChannelIds ? unreadChannelIds.includes(id) : false;
                const isActiveChannel = id === channelId;
                const name = buildDirectMessageName(assignees, clinicUser?.id || '');
                const filteredAssignees = assignees.filter((assignee) => assignee.id !== clinicUser?.id);

                return (
                  <Flex
                    key={id}
                    onClick={partial(goToChannel, id)}
                    px="30px"
                    py={2}
                    cursor="pointer"
                    bg={isActiveChannel ? 'background.primaryAlpha.pressed' : 'transparent'}
                    color={isActiveChannel ? 'text.brand' : 'text.default'}
                    _hover={{
                      bg: 'background.primaryAlpha.hover',
                    }}
                  >
                    <Flex>
                      <Box>
                        <AvatarGroup
                          size="xs"
                          showAddButton={false}
                          avatars={filteredAssignees.map((assignee) => {
                            return {
                              name: `${assignee.firstName} ${assignee.lastName}`,
                              tooltipText: assignee.displayName,
                              src: assignee.profilePic || '',
                              variant: 'outlineAccent',
                              cursor: 'pointer',
                            };
                          })}
                        />
                      </Box>

                      <Text
                        ml="10px"
                        maxW="160px"
                        isTruncated={true}
                        color={isActiveChannel ? 'text.onContract' : 'text.default'}
                      >
                        {name || UNNAMED_CHANNEL_LABEL}
                      </Text>
                    </Flex>

                    {hasUnreadMessages && (
                      <Tooltip label={`This conversation has unread messages`}>
                        <span>
                          <NotificationCounter size="sm" />
                        </span>
                      </Tooltip>
                    )}
                  </Flex>
                );
              })}
            </Flex>
          </Flex>
        </Flex>
      )}

      {showChannelView && (
        <ChannelView
          channelId={channelId}
          refetchChannels={refetchTeamChannels}
          openEditChannelModal={handleEditTeamChannel}
        />
      )}
      {modalName === 'AddTeamChannel' && (
        <AddTeamChannelModal isOpen={isOpen} onClose={handleModalClose} onSuccess={handleAddTeamChannelSuccess} />
      )}
      {modalName === 'EditTeamChannel' && !!channelId && (
        <EditTeamChannelModal channelId={channelId} isOpen={isOpen} onClose={handleModalClose} />
      )}
      {modalName === 'CreateDirectMessage' && (
        <CreateDirectMessageModal onSuccess={handleAddTeamChannelSuccess} isOpen={isOpen} onClose={handleModalClose} />
      )}
    </TeamPageContainer>
  );
};

const TeamPageContainer = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  overflow: auto;
  max-width: 100%;
`;

export default TeamPage;
